import {
  ref,
  computed,
  reactive,
  Ref,
  ComputedRef
} from '@vue/composition-api'
import { capitalize } from 'lodash'

import { auth, toastr } from 'ui-common'
import { navigate } from 'ui-common/lib/utils/router'
import { epworthProps } from './epworth'
import { Symptom, Morbidity } from '@/types'
import { coMorbidityData, symptomOptions } from '@/constants'
import repositories from '@/repositories'

export const contact = reactive({
  firstName: '',
  lastName: '',
  phone: ''
})

export const cpap: Morbidity = reactive({
  name: 'rx_cpap',
  label: 'Have you ever used CPAP before?',
  weight: 4,
  value: null
})
export const symptoms: Ref<Symptom[]> = ref([])
export const coMorbidity: Ref<Morbidity[]> = ref([])

export const submitting = ref(false)

export const result: ComputedRef<{
  epworthWeight: number;
  coMorbidityWeight: number;
  surveyWeight: number;
}> = computed(() => {
  const epworthWeight = epworthProps.list
    .reduce((acc, cur) => acc + cur.selected, 0)
  const coMorbidityWeight = [...coMorbidity.value, cpap]
    .reduce((acc, cur) => acc + (cur.value ? cur.weight : 0), 0)
  const surveyWeight = symptoms.value
    .reduce((acc, cur) => acc + (cur.value ? cur.weight : 0), 0)

  return {
    epworthWeight,
    coMorbidityWeight,
    surveyWeight
  }
})

export const riskLevel: ComputedRef<
  'severe' | 'high' | 'moderate' | 'low'
> = computed(() => {
  const {
    epworthWeight,
    coMorbidityWeight,
    surveyWeight
  } = result.value

  if (surveyWeight > 15 || epworthWeight > 18 || coMorbidityWeight > 3) {
    return 'severe'
  }
  if (surveyWeight > 11 || epworthWeight > 14 || coMorbidityWeight > 2) {
    return 'high'
  }
  if (surveyWeight > 7 || epworthWeight > 9 || coMorbidityWeight > 1) {
    return 'moderate'
  }
  return 'low'
})

export const resetAnswers = () => {
  contact.firstName = ''
  contact.lastName = ''
  contact.phone = ''

  epworthProps.list = []

  symptoms.value = symptomOptions
    .map(option => ({ ...option, value: false }))

  cpap.value = null
  coMorbidity.value = coMorbidityData
    .map(morbidity => ({ ...morbidity, value: false }))

  navigate('/')
}

export const name = computed(() => [
  contact.firstName,
  contact.lastName
].map(name => capitalize(name)).join(' ').trim())

export const submitScreener = async () => {
  try {
    submitting.value = true
    const { docid, userid } = auth.$state.profile

    const screenerData: any = {
      docid: docid || userid,
      userid,
      rxCpap: cpap.value ? cpap.weight : 0,
      epworth: epworthProps.list,

      ...contact
    }

    symptoms.value.forEach(symptom => {
      screenerData[symptom.name] =
        symptom.value ? symptom.weight : 0
    })

    coMorbidity.value.forEach(morbidity => {
      screenerData[morbidity.name] =
        morbidity.value ? morbidity.weight : 0
    })

    await repositories.screener.submitScreener(screenerData)
    toastr.s('Your results have been submitted successfully')
  } catch (err) {
    toastr.e('Failed submitting screener results')
  } finally {
    submitting.value = false
  }
}
